import type { TFieldSetItem } from '~/types/fieldSet/models'
import { isFieldSetField } from '~/types/fieldSet/models'
import type { TDisplayString } from '~/types/models'

const mn = '[cynqueModules/fieldSetKeyIdentifier.ts]'

/**
 * The one value that tells otherwise-identical msServices apart, and where it
 * comes from.
 *
 * A product may name a **fieldSet**; a fieldSet may flag exactly one of its
 * fields as the key identifier (`isKeyIdentifierField`); an msService carries
 * its own answers to that fieldSet as the `fieldSetData` JSON blob. When all
 * three hold, the flagged field's value is the msService's key identifier --
 * `phoneNumber` for a POTS line, `circuitId` for a circuit, `serialNumber` for
 * an SDWAN box.
 *
 * ⚠ THE JSON KEY IS THE FIELD'S `name`, NOT ITS `label`. `fieldSetData` is
 * built from the entity-mask items in
 * `services/fieldSets.ts convertFieldSetFieldsToEntityMaskItems`, which sets
 * `entityMaskItem.name = name` -- so the blob reads
 * `{"phoneNumber": "(555) 444-1111"}`, never `{"Phone Number": ...}`. Two
 * places in the tree hold the label instead and are NOT usable here:
 *   - `pages/Field Sets/[id].vue propagateKeyIdentifierFieldNameAndNumFields`
 *     assigns `keyIdentifierField.label` onto `overviewData.fieldSet` for the
 *     FieldSetHeaderCard's "Key Identifier" column (display only -- it is never
 *     PATCHed, so `field_sets.key_identifier_field_name` is NULL server-side).
 *   - `products.key_identifier_field_name`, whose mock values include `'Model'`
 *     and `'# Seats'`. That column is NULL for every product on the real API
 *     and has no input-spec field, so it is dead; the fieldSet replaced it.
 *
 * These two functions are the whole derivation, kept pure and I/O-free so the
 * app and the MSW mock share one implementation and cannot drift. The app gets
 * its fields over HTTP (see ./fieldSetKeyIdentifierCache.ts); the mock reads
 * its own `getFieldSetFieldListDataForFieldSet` directly.
 */

/**
 * The `name` of the field this fieldSet flags as its key identifier, if any.
 *
 * At most one field per fieldSet carries the flag --
 * `ensureUniquenessAndPropagateFlagChange` enforces that on both the client
 * (the Field Sets page) and the mock (`processAfterAddOrUpdateFieldSetFieldListData`).
 * `.find()` matches that: first flagged field wins if the data ever disagrees.
 *
 * Only an `IFieldSetField` can be flagged -- a divider, subtitle or spacer has
 * no `isKeyIdentifierField` property at all -- hence the `isFieldSetField`
 * guard rather than a bare property read.
 */
export function getKeyIdentifierFieldNameFromFields(
  fields: TFieldSetItem[] | undefined
): string | undefined {
  if (!fields) {
    return undefined
  }
  const keyIdentifierField = fields.find(
    (field) => isFieldSetField(field) && field.isKeyIdentifierField === true
  )
  return keyIdentifierField?.name || undefined
}

/**
 * The value `fieldName` holds inside an msService's `fieldSetData` blob.
 *
 * Returns undefined for every "we cannot answer" case -- no field name, no
 * blob, unparseable blob, key absent, value empty -- so that a caller can use
 * `undefined` as its single "leave whatever is already there alone" signal.
 * That matters: `MSService()` is called as
 * `Object.assign(msService, MSService(msService, ...))`, so assigning an
 * undefined here would WIPE a value the API (or the mock) put on the wire.
 */
export function readKeyIdentifierValueFromFieldSetData(
  fieldSetData: string | undefined,
  fieldName: string | undefined
): TDisplayString | undefined {
  const fn = `${mn} ${readKeyIdentifierValueFromFieldSetData.name} |`
  if (!fieldName || !fieldSetData) {
    return undefined
  }
  let parsed: Record<string, unknown>
  try {
    parsed = JSON.parse(fieldSetData)
  } catch (e) {
    console.error(`${fn} couldn't parse fieldSetData:`, fieldSetData, e)
    return undefined
  }
  const value = parsed?.[fieldName]
  if (value === undefined || value === null || value === '') {
    return undefined
  }
  return String(value)
}
